import { ReactElement, useEffect, useState, useCallback } from 'react'
import { ContextProps, createContext } from '../common/contextHelper'
import { useAuthContext } from './AuthContext'
import { profileApi } from '../services/profileApi'
import type { UserProfile, UpdateProfileRequest } from '../types/profile'

interface ProfileContextState {
  profile: UserProfile | null
  isLoading: boolean
  error: string | null
  refreshProfile: () => Promise<void>
  updateProfile: (data: UpdateProfileRequest) => Promise<UserProfile>
}

const [Provider, useProfileContext] = createContext<ProfileContextState>(module.filename)

export { useProfileContext }

export function ProfileProvider({ children }: ContextProps): ReactElement {
  const { email, isAuthorized } = useAuthContext()

  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchProfile = useCallback(async () => {
    // Only fetch when both email is set AND user is authorized
    // This prevents race conditions during login
    if (!email || !isAuthorized) {
      setProfile(null)
      return
    }

    const currentPath = window.location.pathname
    const isRegistrationFlow =
      currentPath === '/registration/complete' ||
      currentPath === '/auth/callback' ||
      currentPath === '/register' ||
      currentPath === '/reset-password' ||
      currentPath === '/forgot-password'

    if (isRegistrationFlow) {
      return
    }

    const token = localStorage.getItem('token')
    if (!token || token === 'undefined' || token === 'null') {
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const data = await profileApi.getProfile()
      setProfile(data)
    } catch (err: any) {
      console.error('ProfileContext: Failed to fetch profile:', err)
      setError(err.message || 'Failed to load profile')
    } finally {
      setIsLoading(false)
    }
  }, [email, isAuthorized])

  useEffect(() => {
    fetchProfile()
  }, [fetchProfile])

  const refreshProfile = async () => {
    await fetchProfile()
  }

  const updateProfile = async (data: UpdateProfileRequest): Promise<UserProfile> => {
    setIsLoading(true)
    setError(null)

    try {
      const updated = await profileApi.updateProfile(data)
      setProfile(updated)
      return updated
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to update profile'
      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  return <Provider value={{ profile, isLoading, error, refreshProfile, updateProfile }}>{children}</Provider>
}
